import { isInstructionOverdue } from '@components/utilities/Bookings/helpers';
import { instructionStrings } from '@data/instruction';
import type {
  CustomsInstruction,
  CustomsInstructionInsuranceForm,
  CustomsInstructionInsurance,
  CustomsInstructionForm,
  CustomsInstructionSpecialNoteItemForm,
  CustomsInstructionSpecialNoteItem,
  PatchCustomsInstruction,
} from '@models/next/CustomsInstruction';
import { FileTypeHelper, Upload } from '@models/next/FileType';
import { empty } from '@monoid-dev/mobx-zod-form';
import { makeMessagesEitherLeftIf } from '@utils/errors';
import { mapOptional } from '@utils/types';

export class CustomsInstructionHelper {
  static mapToForm(v: CustomsInstruction): CustomsInstructionForm {
    return {
      ...v,
      commodities: v.commodities.map(({ id }) => id),
      custom_files: v.custom_files.map(FileTypeHelper.remoteToLocal),
      insurance: v.insurance ? this.mapInsuranceToForm(v.insurance) : empty,
      special_note: v.special_note.map(this.mapSpecialNoteToForm),
      rcep_file: v.rcep_file ? FileTypeHelper.remoteToLocal(v.rcep_file) : undefined,
      action: 'attempt',
    };
  }

  static mapInsuranceToForm(v: CustomsInstructionInsurance): CustomsInstructionInsuranceForm {
    if (v.tag === 'NA') {
      return v;
    } else if (v.tag === 'BLANKET') {
      return v;
    } else {
      // v.tag === 'INDIVIDUAL'
      return {
        ...v,
        individual_insurance_files: v.individual_insurance_files.map(FileTypeHelper.remoteToLocal),
      };
    }
  }

  static mapSpecialNoteToForm(
    v: CustomsInstructionSpecialNoteItem,
  ): CustomsInstructionSpecialNoteItemForm {
    return v;
  }

  static getCurrentVisibleStatus(v: Pick<CustomsInstruction, 'status'>) {
    return v.status.filter((v) => v.is_visible).at(-1);
  }

  static isOverdue(v: Pick<CustomsInstruction, 'status' | 'cutoff_date'>) {
    return (
      this.getCurrentVisibleStatus(v)?.name === '未送信' && isInstructionOverdue(v.cutoff_date)
    );
  }

  static async uploadForm(
    v: CustomsInstructionForm,
    uploader: Upload,
  ): Promise<PatchCustomsInstruction> {
    return {
      ...v,
      custom_files: await FileTypeHelper.uploadLocalArrayToRemote(v.custom_files, uploader),
      insurance: await this.uploadInsuranceForm(v.insurance, uploader),
      rcep_file:
        (await mapOptional(v.rcep_file, (d) => FileTypeHelper.uploadLocal(d, uploader))) ?? null,
    };
  }

  static async uploadInsuranceForm(
    v: CustomsInstructionInsuranceForm,
    uploader: Upload,
  ): Promise<CustomsInstructionInsurance> {
    if (v.tag === 'NA') {
      return v;
    } else if (v.tag === 'BLANKET') {
      return v;
    } else if (v.tag === 'INDIVIDUAL') {
      return {
        ...v,
        individual_insurance_files: await FileTypeHelper.uploadLocalArrayToRemote(
          v.individual_insurance_files,
          uploader,
        ),
      };
    }
    // v is never
    return v;
  }

  static canConfirm(v: CustomsInstruction) {
    return makeMessagesEitherLeftIf(this.getCurrentVisibleStatus(v)?.name === '確認済み', {
      messages: [instructionStrings.errorAlreadyConfirmed],
    });
  }

  static canRequireChange(v: CustomsInstruction) {
    return makeMessagesEitherLeftIf(this.getCurrentVisibleStatus(v)?.name === '確認済み', {
      messages: [instructionStrings.errorAlreadyConfirmed],
    });
  }
}
